import { createAsyncThunk } from "@reduxjs/toolkit";
import adminService from "../../../api/services/admin";

export const getDashboardStats = createAsyncThunk(
  "admin/getDashboardStats",
  async (timeframe = '7days', { rejectWithValue }) => {
    try {
      const response = await adminService.getDashboardStats(timeframe);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch dashboard stats"
      );
    }
  }
);

export const getRecentActivities = createAsyncThunk(
  "admin/getRecentActivities",
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await adminService.getRecentActivities(limit);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch recent activities"
      );
    }
  }
);

export const getSalesChartData = createAsyncThunk(
  "admin/getSalesChartData",
  async (timeframe = '7days', { rejectWithValue }) => {
    try {
      const response = await adminService.getSalesChartData(timeframe);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch sales chart data"
      );
    }
  }
);

export const getCoursePerformance = createAsyncThunk(
  "admin/getCoursePerformance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getCoursePerformance();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch course performance"
      );
    }
  }
);

export const getRevenueAnalytics = createAsyncThunk(
  "admin/getRevenueAnalytics",
  async (period = 'monthly', { rejectWithValue }) => {
    try {
      const response = await adminService.getRevenueAnalytics(period);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch revenue analytics"
      );
    }
  }
);

// User Management
export const getAllUsers = createAsyncThunk(
  "admin/getAllUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getAllUsers(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

export const getUserById = createAsyncThunk(
  "admin/getUserById",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserById(userId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  "admin/updateUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUser(userId, userData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);

export const deleteUser = createAsyncThunk(
  "admin/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await adminService.deleteUser(userId);
      return { userId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);

// Course Management
export const getAllCourses = createAsyncThunk(
  "admin/getAllCourses",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getAllCourses(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courses"
      );
    }
  }
);

export const getCourseById = createAsyncThunk(
  "admin/getCourseById",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await adminService.getCourseById(courseId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch course"
      );
    }
  }
);

export const createCourse = createAsyncThunk(
  "admin/createCourse",
  async (courseData, { rejectWithValue }) => {
    try {
      const response = await adminService.createCourse(courseData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create course"
      );
    }
  }
);

export const updateCourse = createAsyncThunk(
  "admin/updateCourse",
  async ({ courseId, courseData }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateCourse(courseId, courseData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update course"
      );
    }
  }
);

export const deleteCourse = createAsyncThunk(
  "admin/deleteCourse",
  async (courseId, { rejectWithValue }) => {
    try {
      const response = await adminService.deleteCourse(courseId);
      return { courseId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete course"
      );
    }
  }
);

// Orders async thunks
export const getAllOrders = createAsyncThunk(
  "admin/getAllOrders",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getAllOrders(filters);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch orders"
      );
    }
  }
);

export const getOrderById = createAsyncThunk(
  "admin/getOrderById",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await adminService.getOrderById(orderId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order"
      );
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  "admin/updateOrderStatus",
  async ({ orderId, status, paymentStatus }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateOrderStatus(orderId, { status, paymentStatus });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update order"
      );
    }
  }
);

export const deleteOrder = createAsyncThunk(
  "admin/deleteOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await adminService.deleteOrder(orderId);
      return { orderId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete order"
      );
    }
  }
);

// Promo Code Management
export const getAllPromoCodes = createAsyncThunk(
  "admin/getAllPromoCodes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getAllPromoCodes(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch promo codes"
      );
    }
  }
);

export const createPromoCode = createAsyncThunk(
  "admin/createPromoCode",
  async (promoData, { rejectWithValue }) => {
    try {
      const response = await adminService.createPromoCode(promoData);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create promo code"
      );
    }
  }
);

export const deletePromoCode = createAsyncThunk(
  "admin/deletePromoCode",
  async (promoId, { rejectWithValue }) => {
    try {
      const response = await adminService.deletePromoCode(promoId);
      return { promoId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete promo code"
      );
    }
  }
);